import React, { useMemo, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiEye,
  FiCheckCircle,
  FiXCircle,
  FiInfo,
  FiX,
} from 'react-icons/fi';
import { adminApi, MasterTraderApplication } from '../../api';
import { Loader } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { showToast } from '../../utils/toast';

const PAGE_SIZE = 20;

const timeAgo = (input: string) => {
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

const AdminMasterTraderApplications: React.FC = () => {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved'>('pending');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState<
    (MasterTraderApplication & { pnlUpload?: { url: string } }) | null
  >(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'master-trader-applications', statusFilter],
    queryFn: () => adminApi.getMasterTraderApplications(statusFilter),
  });

  const applications = useMemo<MasterTraderApplication[]>(() => data?.applications || [], [data?.applications]);

  useEffect(() => setPage(1), [applications.length, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(applications.length / PAGE_SIZE));
  const paged = applications.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const approveMutation = useMutation({
    mutationFn: (applicationId: string) => adminApi.approveMasterTraderApplication(applicationId),
    onSuccess: (res) => {
      showToast('success', res.message || 'Application approved');
      queryClient.invalidateQueries({ queryKey: ['admin', 'master-trader-applications'] });
      setDetailsOpen(false);
      setDetails(null);
    },
    onError: (err: any) => {
      showToast('error', err?.response?.data?.error || err.message || 'Failed to approve application');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (applicationId: string) => adminApi.cancelMasterTraderApplication(applicationId),
    onSuccess: (res) => {
      showToast('success', res.message || 'Application cancelled');
      queryClient.invalidateQueries({ queryKey: ['admin', 'master-trader-applications'] });
      setDetailsOpen(false);
      setDetails(null);
    },
    onError: (err: any) => {
      showToast('error', err?.response?.data?.error || err.message || 'Failed to cancel application');
    },
  });

  const handleViewDetails = async (applicationId: string) => {
    try {
      setDetailsLoading(true);
      const res = await adminApi.getMasterTraderApplicationDetails(applicationId);
      if (!res?.success) {
        showToast('error', 'Failed to load details');
        return;
      }
      setDetails(res.application || null);
      setDetailsOpen(true);
    } catch (err: any) {
      showToast('error', err?.response?.data?.error || err.message || 'Failed to load details');
    } finally {
      setDetailsLoading(false);
    }
  };

  if (isLoading) return <Loader message="Loading master applications..." />;
  if (error) return <ErrorDisplay error="Failed to load master applications" />;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-bold text-foreground">Master Trader Applications</h3>
        <p className="text-sm text-muted">Showing {statusFilter} submissions uploaded by users.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setStatusFilter('pending')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
              statusFilter === 'pending'
                ? 'border-primary/40 bg-primary/10 text-primary'
                : 'border-border bg-background/30 text-muted hover:bg-background/50'
            }`}
          >
            Pending
          </button>
          <button
            type="button"
            onClick={() => setStatusFilter('approved')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition ${
              statusFilter === 'approved'
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400'
                : 'border-border bg-background/30 text-muted hover:bg-background/50'
            }`}
          >
            Approved
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-border bg-surface min-w-0">
        <div className="table-scroll">
          <table className="w-full min-w-[1100px] text-left text-sm">
            <thead className="border-b border-border bg-background/60 text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Platform</th>
                <th className="px-4 py-3">Experience</th>
                <th className="px-4 py-3">Communication</th>
                <th className="px-4 py-3">PnL File</th>
                <th className="px-4 py-3">Payment</th>
                <th className="px-4 py-3">Webull API</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paged.map((app) => (
                <tr key={app._id} className="hover:bg-background/40">
                  <td className="px-4 py-4">
                    <p className="font-semibold text-foreground">{app.fullName}</p>
                    <p className="text-xs text-muted">{app.email}</p>
                    <p className="text-xs text-muted">{app.phone}</p>
                  </td>
                  <td className="px-4 py-4">{app.tradingPlatform}</td>
                  <td className="px-4 py-4 text-muted">{app.tradingExperience || '—'}</td>
                  <td className="px-4 py-4 text-muted">{app.communicationMethod || '—'}</td>
                  <td className="px-4 py-4">
                    {app.pnlUpload?.url ? (
                      <a
                        href={app.pnlUpload.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs font-semibold text-primary hover:bg-background"
                      >
                        <FiFileText />
                        View
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-4 text-muted max-w-[220px]">
                    <div className="truncate" title={app.paymentDetails || ''}>
                      {app.paymentDetails || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-muted max-w-[220px]">
                    {app.tradingPlatform === 'webull' ? (
                      <div className="flex flex-col gap-1">
                        <div className="truncate" title={app.webullApiKey || ''}>
                          Key: {app.webullApiKey || '—'}
                        </div>
                        <div className="truncate" title={app.webullApiSecret || ''}>
                          Secret: {app.webullApiSecret || '—'}
                        </div>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-4 text-muted">
                    {app.createdAt ? timeAgo(app.createdAt) : '—'}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleViewDetails(app._id)}
                        disabled={detailsLoading}
                        className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs font-semibold text-primary hover:bg-background disabled:opacity-60"
                      >
                        <FiEye className="h-4 w-4" />
                        {detailsLoading ? 'Loading...' : 'Details'}
                      </button>
                      {app.status === 'pending' && (
                        <>
                          <button
                            onClick={() => approveMutation.mutate(app._id)}
                            disabled={approveMutation.isPending}
                            className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
                          >
                            <FiCheckCircle className="h-4 w-4" />
                            {approveMutation.isPending ? '...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => cancelMutation.mutate(app._id)}
                            disabled={cancelMutation.isPending}
                            className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
                          >
                            <FiXCircle className="h-4 w-4" />
                            {cancelMutation.isPending ? '...' : 'Cancel'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {applications.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-muted">No {statusFilter} applications found.</p>
        )}
      </div>

      {detailsOpen && details && (() => {
        const pnlUrl = details.pnlUpload?.url || '';
        const pnlMime = String(details.pnlUpload?.mimeType || '').toLowerCase();
        const isImage =
          pnlMime.startsWith('image/') ||
          /\.(jpg|jpeg|png|gif|webp)$/i.test(pnlUrl);

        return (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-3 sm:p-4 backdrop-blur-sm">
            <div className="w-full max-w-3xl max-h-[90vh] rounded-2xl border border-border bg-surface-elevated shadow-2xl overflow-hidden flex flex-col">

              {/* Header */}
              <div className="flex items-start justify-between border-b border-border p-4 sm:p-6 shrink-0 max-h-[45vh] overflow-y-auto">
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg sm:text-xl font-bold text-foreground break-anywhere">{details.fullName}</h3>

                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <FiInfo className="h-4 w-4 text-primary shrink-0" />
                      <a href={`mailto:${details.email}`} className="text-sm text-primary hover:underline truncate">{details.email}</a>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <FiInfo className="h-4 w-4 text-emerald-400 shrink-0" />
                      <span className="text-sm font-medium text-foreground break-anywhere min-w-0">{details.phone}</span>
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <FiInfo className="h-4 w-4 text-amber-400 shrink-0" />
                      <span className="text-sm font-medium text-foreground break-anywhere min-w-0">{details.communicationMethod || '—'}</span>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
                      {details.tradingPlatform === 'webull' ? 'Webull' : 'Forex'}
                    </span>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${
                      details.status === 'pending' ? 'bg-amber-500/15 text-amber-400'
                        : details.status === 'approved' ? 'bg-emerald-500/15 text-emerald-400'
                        : 'bg-red-500/15 text-red-400'
                    }`}>
                      {details.status}
                    </span>
                    {details.createdAt && (
                      <span className="rounded-full border border-border px-3 py-1 text-xs text-muted">
                        {timeAgo(details.createdAt)}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => { setDetailsOpen(false); setDetails(null); }}
                  className="rounded-lg p-2 text-muted transition hover:bg-surface hover:text-foreground ml-2 sm:ml-4 shrink-0"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-4 sm:p-6 overflow-y-auto flex-1 min-h-0 space-y-4">
                {detailsLoading ? (
                  <Loader message="Loading..." />
                ) : (
                  <>
                    {/* Info cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <InfoCard label="Trading Experience" value={details.tradingExperience} />
                      <InfoCard label="Communication" value={details.communicationMethod} />
                      <InfoCard label="Payment Details" value={details.paymentDetails} />
                    </div>

                    {/* PnL Upload — show image inline if applicable */}
                    <div className="rounded-xl border border-border bg-surface p-4">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-3">
                        PnL Screenshot / File
                      </p>

                      {pnlUrl ? (
                        <>
                          {isImage ? (
                            <a href={pnlUrl} target="_blank" rel="noreferrer" className="block">
                              <img
                                src={pnlUrl}
                                alt="PnL Screenshot"
                                className="w-full max-h-[400px] object-contain rounded-lg border border-border bg-background/30"
                              />
                            </a>
                          ) : (
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                              <p className="text-sm text-foreground break-anywhere min-w-0">
                                {details.pnlUpload?.originalName || 'Uploaded file'}
                              </p>
                              <a
                                href={pnlUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-white hover:opacity-90 shrink-0"
                              >
                                <FiFileText />
                                Open file
                              </a>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-muted">No file uploaded</p>
                      )}
                    </div>

                    {/* Webull API credentials */}
                    {details.tradingPlatform === 'webull' && (
                      <div className="rounded-xl border border-border bg-surface p-4">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-3">
                          Webull API Credentials
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-muted mb-1">API Key</p>
                            <pre className="rounded-lg border border-border bg-background/40 p-3 text-xs text-foreground overflow-x-auto select-all">
{details.webullApiKey || '—'}
                            </pre>
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-muted mb-1">API Secret</p>
                            <pre className="rounded-lg border border-border bg-background/40 p-3 text-xs text-foreground overflow-x-auto select-all">
{details.webullApiSecret || '—'}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 border-t border-border p-4 sm:p-5 shrink-0">
                {details.status === 'pending' ? (
                  <>
                    <button
                      type="button"
                      disabled={cancelMutation.isPending}
                      onClick={() => {
                        if (!details?._id) return;
                        if (!window.confirm('Cancel this application?')) return;
                        cancelMutation.mutate(details._id);
                      }}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-500 px-5 py-2.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
                    >
                      <FiXCircle className="h-4 w-4" />
                      {cancelMutation.isPending ? 'Cancelling...' : 'Reject'}
                    </button>

                    <button
                      type="button"
                      disabled={approveMutation.isPending}
                      onClick={() => {
                        if (!details?._id) return;
                        if (!window.confirm('Approve this application?')) return;
                        approveMutation.mutate(details._id);
                      }}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-60"
                    >
                      <FiCheckCircle className="h-4 w-4" />
                      {approveMutation.isPending ? 'Approving...' : 'Approve'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setDetailsOpen(false);
                      setDetails(null);
                    }}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-border px-5 py-2.5 text-xs font-semibold text-foreground hover:bg-surface"
                  >
                    <FiX className="h-4 w-4" />
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {totalPages > 1 && applications.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 rounded-2xl border border-border bg-surface px-4 py-3">
          <p className="text-xs text-muted">
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, applications.length)} of {applications.length}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg p-2 text-muted transition hover:bg-background hover:text-foreground disabled:opacity-40"
            >
              <FiChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`min-w-[32px] rounded-lg px-2 py-1 text-xs font-semibold transition ${
                  n === page ? 'bg-primary text-white' : 'text-muted hover:bg-background hover:text-foreground'
                }`}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded-lg p-2 text-muted transition hover:bg-background hover:text-foreground disabled:opacity-40"
            >
              <FiChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

function InfoCard({ label, value }: { label: string; value?: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted">{label}</p>
      <p className="mt-2 whitespace-pre-wrap break-words break-anywhere text-sm font-medium text-foreground">
        {value || '—'}
      </p>
    </div>
  );
}

export default AdminMasterTraderApplications;

